import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';

import { CapicheMaterialModule } from 'capiche-material';

import { LoginData } from '../../interfaces/login-data.interface';
import { AuthService } from '../../services/auth/auth.service';
import { LoginFormComponent } from '../../components/login-form/login-form.component';
import { StudentLoginData } from '../../capiche-auth.config';

import { CapicheLoggerService } from 'capiche-logger';

@Component({
  selector: 'capiche-auth-login',
  imports: [CommonModule, RouterModule, LoginFormComponent, CapicheMaterialModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent implements OnInit, OnDestroy {
  logger = new CapicheLoggerService('login-page.component:', false);
  private authService = inject(AuthService);
  private matIconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);

  // this is a temporary workaround that does not allow to show the signInWithGoogle button in the extension
  // see: https://capiche.atlassian.net/browse/CSW-1046
  // sign-in-with-google-requires-adaption-for-chrome-extension-manifestv3
  showSignInWithGoogle: boolean = this.authService.config.googleSignInHandler ? false : true; // hide signInWithGoogle if extensions for now

  msg: string = '';
  loading: boolean = false;
  private routerSub: Subscription | undefined;

  constructor() {
    this.logger.log('constructor');
    this.matIconRegistry.addSvgIcon(
      'google-logo',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/google-logo.svg'),
    );
  }

  ngOnInit(): void {
    this.logger.log('ngOnInit');
    this.routerSub = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.loading = false;
      this.logger.log('navigation end: loading:', this.loading);
    });
  }

  ngOnDestroy(): void {
    this.logger.log('ngOnDestroy');
    this.routerSub?.unsubscribe();
  }

  onLogin(loginData: LoginData) {
    this.loading = true;
    this.authService
      .login(loginData)
      .then(() => this.logger.log('login in .then'))
      .catch((e) => {
        console.error(e.message);
        switch (e.code) {
          case 'auth/user-not-found':
            this.msg = 'User not found';
            break;
          case 'auth/wrong-password':
            this.msg = 'Wrong password';
            break;
          case 'auth/invalid-email':
            this.msg = 'Invalid email address';
            break;
          default:
            this.logger.error('login error:', e);
        }
        this.loading = false; // fallback in case of failure
      });
  }

  onStudentLogin(data: StudentLoginData) {
    this.loading = true;
    this.msg = '';
    this.authService
      .studentLogin(data)
      .then(() => this.logger.log('student login .then'))
      .catch((e) => {
        console.error(e?.message ?? e);
        const status = e?.status;
        this.msg = status === 401
          ? 'Username or password not recognised. Ask your teacher to reset it.'
          : 'Something went wrong. Please try again.';
        this.loading = false; // fallback in case of failure
      });
  }

  onLoginWithGoogle() {
    this.loading = true;
    this.logger.log('login with google loading:', this.loading);
    this.authService
      .loginWithGoogle()
      .then(() => this.logger.log('logged in with google .then'))
      .catch((e) => {
        console.error('logged in with google error:', e.message);
        this.msg = 'Google login failed. Please try again.';
        this.loading = false;
      });
  }
}
